/**
 * @module Change Friendship
 * @since 3.78.0
 * @author Jacob Viertel <jv@onscreen.net>
 *
 * This component shows a description of the current friendship status
 * of an amateur and buttons to change that status.
 */
import {setText} from '@acng/frontend-bounty';
import {Error} from '@acng/frontend-bounty/std/error.js';
import {all} from '@acng/frontend-bounty/std/control.js';
import {hide, show} from '@acng/frontend-bounty/style/element.js';
import {ctxFriendship} from '../context/friendships.js';
import {ElementSetup, Engine, NODES, task} from '@acng/frontend-stargazer';
import {WatchGroup} from '@acng/frontend-relativity';
import {guard} from '@acng/frontend-rubicon';

import {feature} from '../service/template.js';
import {ctxAmateur} from 'acng/amateurPool/context/amateur.js';
import {coreFeature} from 'acng/core/config/feature.js';
import {put} from '../service/http.js';
import {
  POSSIBLE_STATES,
  STATE_CONFIRMED,
  STATE_DENIED,
  STATE_PENDING,
  STATE_REQUESTED,
  STATE_RESET,
} from '../model/friendship.js';
import {def, con, dis, HTMLElement} from '@acng/frontend-bounty/dom/custom.js';
import {t} from 'acng/locale/config/translate.js';
import {get} from '../service/cache.js';

const MODULE = 'friend/widget/operations';
const VERBOSE = false;
DEBUG: if (VERBOSE) console.warn('Import verbose', MODULE);

/**
 * @group DOM Element
 */
export const FRIEND_OPERATIONS = 'onsw-friend-operations';

ElementSetup(FRIEND_OPERATIONS, (name) => {
  const watch = WatchGroup(name, ctxAmateur, ctxFriendship);
  const transclude = Engine.Template(name, undefined, [feature.id, name]);

    class ChangeFriendship extends HTMLElement {
      /**
       * @type {Record<string, ?import('@acng/frontend-stargazer').Engine>}
       */
      static r;

      /**
       * Preload a bunch of translations
       *
       * @param {Element} element
       */
      static async setup(element) {
        /**
         * @type {string[]}
         */
        const keys = [];
        for (const s of POSSIBLE_STATES) {
          keys.push(`explain_${s}`);
        }
        for (const [k, v] of Object.entries(opMap.yes)) {
          keys.push(`${k}_${v}`);
        }
        for (const [k, v] of Object.entries(opMap.no)) {
          keys.push(`${k}_${v}`);
        }

        this.r = Object.fromEntries(
          (await all(keys.map((k) => Engine.root.render('translate', 'friend', k)))).map((rendering) => [
            rendering.id,
            rendering,
          ])
        );
      }

      #engine = Engine.root;

      yes() {
        this.#click(opMap.yes);
      }

      no() {
        this.#click(opMap.no);
      }

      /**
       * @param {typeof opMap["yes"] | typeof opMap["no"]} transition
       */
      async #click(transition) {
        ASSERT: guard(this.#engine.nodes, TEMPLATE);

        const {yesButton, noButton, text} = this.#engine.nodes;
        const amateur = ctxAmateur.value(this);

        if (!amateur) {
          throw Error();
        }

        hide(yesButton);
        hide(noButton);
        setText(text, null);

        const currentFriendshipState = get(amateur);
        const nextFriendshipState = transition[currentFriendshipState];

        let confirmed = true;
        if (currentFriendshipState == 'confirmed' && nextFriendshipState == 'reset') {
          const question = await t([feature.id, 'confirmEnd'], {nickname: amateur?.getNickname()});
          confirmed = await coreFeature.popup(
            this,
            'confirm',
            /**
             * @param {(value?: boolean) => void} close
             */
            (close) => ({question, yes: () => close(true), no: () => close(false)})
          );
        }
        if (confirmed && nextFriendshipState) {
          await put(this, amateur, nextFriendshipState);
        } else {
          // Restore buttons
          if (opMap.yes[currentFriendshipState]) {
            show(yesButton);
          }
          if (opMap.no[currentFriendshipState]) {
            show(noButton);
          }
        }
      }

      [con]() { // after template appended
        this.#engine = transclude(this);
        this.#engine.toElement(this, this);

        watch(this, (element, [amateur]) => {
          ASSERT: guard(this.#engine.nodes, TEMPLATE);

          if (!amateur) {
            return;
          }

          const s = get(amateur);
          const {yesButton, noButton, yesLabel, noLabel, text} = this.#engine.nodes;

          /*
          if (error) {
            setText(text, error.message);
            hide(yesButton);
            hide(noButton);
            return;
          }
          */

          setText(
            text,
            ChangeFriendship.r[`explain_${s}`]?.toText({nickname: amateur.getNickname()}) ?? null
          );
          if (s in opMap.yes) {
            setText(yesLabel, ChangeFriendship.r[`${s}_${opMap.yes[s]}`]?.toText() ?? null);
            show(yesButton);
          } else {
            hide(yesButton);
          }
          if (s in opMap.no) {
            setText(noLabel, ChangeFriendship.r[`${s}_${opMap.no[s]}`]?.toText() ?? null);
            show(noButton);
          } else {
            hide(noButton);
          }
        });
      }

      [dis]() {
        this.#engine.disconnect();
      }
    }

  def(name, ChangeFriendship);

  const ACTION_ACCEPT = 'accept';
  const ACTION_REQUEST = 'request';
  const ACTION_REJECT = 'reject';
  const ACTION_RESET = 'reset';

  /**
   * @type {Record<"yes" | "no", {[key in Valid<typeof import('../model/friendship').FRIENDSHIP_STATE>]?: string}>}
   */
  const opMap = {
    yes: {
      [STATE_PENDING]: ACTION_ACCEPT,
      [STATE_RESET]: ACTION_REQUEST,
    },
    no: {
      [STATE_CONFIRMED]: ACTION_RESET,
      [STATE_PENDING]: ACTION_REJECT,
      [STATE_DENIED]: ACTION_RESET,
      [STATE_REQUESTED]: ACTION_RESET,
    },
  };

  return (element, compiler) => task(compiler, ChangeFriendship.setup(element));
});

const TEMPLATE = /* @__PURE__ */ (() =>
  NODES({
    yesButton: HTMLButtonElement,
    noButton: HTMLButtonElement,
    text: Text,
    yesLabel: Text,
    noLabel: Text,
  }))();
